"use client";

import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import CONSTANTS from "../utils/constants";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const fetchNotifications = async () => {
  const response = await fetch(`${CONSTANTS.baseRoute}/push/notifications/ios`);
  return await response.json();
};

const fetchDevices = async () => {
  const response = await fetch(`${CONSTANTS.baseRoute}/push/devices/ios`);
  return await response.json();
};

const formatDate = (isoString) => {
  if (!isoString) return "";
  const date = new Date(isoString);
  return date.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default function PushPage() {
  const { t } = useTranslation();
  const { register, handleSubmit, reset } = useForm();
  const [devicesCount, setDevicesCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const navigate = useNavigate();

  const getData = async () => {
    try {
      const notifyRes = await fetchNotifications();
      const devicesRes = await fetchDevices();

      const general =
        notifyRes?.notifications?.filter((item) => !item.token) || [];

      setNotifications(general);
      setDevicesCount(devicesRes?.devices || 0);
    } catch (err) {
      console.error(err);
      setMessage({
        type: "danger",
        text: t("push.loadError"),
      });
    }
  };

  useEffect(() => {
    getData();
  }, []);

  const createNewPushNotification = async (formData) => {
    setMessage({ type: "", text: "" });

    if (!formData.title?.trim() || !formData.body?.trim()) {
      setMessage({
        type: "danger",
        text: t("push.fillAllFields"),
      });
      return;
    }

    try {
      setIsLoading(true);
      const response = await fetch(`${CONSTANTS.baseRoute}/push/send/ios`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: formData.title,
          body: formData.body,
        }),
      });

      if (response.ok) {
        setMessage({
          type: "success",
          text: t("push.success"),
        });
        reset();
        getData();
        setTimeout(() => setMessage({ type: "", text: "" }), 3000);
      } else {
        const errData = await response.json();
        setMessage({
          type: "danger",
          text: errData.message || t("push.sendError"),
        });
      }
    } catch {
      setMessage({
        type: "danger",
        text: t("push.connectionError"),
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="admin-main py-2 px-3">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="d-flex align-items-center gap-2">
          <button className="btn btn-light" onClick={() => navigate(-1)}>
            <i className="bi bi-chevron-left"></i>
          </button>
          <button className="btn btn-light" onClick={() => navigate(+1)}>
            <i className="bi bi-chevron-right"></i>
          </button>
          <h5 className="fw-bold mb-0">{t("push.title")}</h5>
        </div>

        <div className="badge bg-primary fs-6 p-2 d-flex align-items-center gap-2">
          <span>{t("push.deviceCount")}:</span>
          <span className="fw-bold">{devicesCount}</span>
        </div>
      </div>

      <hr />

      {message.text && (
        <div className={`alert alert-${message.type} alert-dismissible fade show`}>
          {message.text}
          <button
            type="button"
            className="btn-close"
            onClick={() => setMessage({ type: "", text: "" })}
          ></button>
        </div>
      )}

      <div className="row g-4">
        {/* HISTORY */}
        <div className="col-12 col-lg-7">
          <div className="card shadow-sm h-100">
            <div className="card-header bg-white fw-bold py-3">
              <i className="bi bi-clock-history text-primary me-2"></i>
              {t("push.history")}
            </div>

            <div className="card-body p-0" style={{ maxHeight: 600, overflowY: "auto" }}>
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light sticky-top">
                    <tr>
                      <th>{t("push.table.title")}</th>
                      <th>{t("push.table.body")}</th>
                      <th>{t("push.table.date")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {notifications.length ? (
                      notifications.map((item) => (
                        <tr key={item.id}>
                          <td className="text-primary fw-medium">{item.title}</td>
                          <td className="text-muted small">{item.body}</td>
                          <td className="small text-nowrap">
                            {formatDate(item.created_at)}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3" className="text-center py-5 text-muted">
                          {t("push.noData")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* SEND */}
        <div className="col-12 col-lg-5">
          <div className="card shadow-sm">
            <div className="card-header bg-white fw-bold py-3">
              <i className="bi bi-send-plus text-success me-2"></i>
              {t("push.sendNew")}
            </div>

            <div className="card-body">
              <form onSubmit={handleSubmit(createNewPushNotification)}>
                <div className="mb-3">
                  <label className="form-label fw-bold">
                    {t("push.form.title")}
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t("push.form.titlePlaceholder")}
                    {...register("title")}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-bold">
                    {t("push.form.body")}
                  </label>
                  <textarea
                    className="form-control"
                    rows={5}
                    placeholder={t("push.form.bodyPlaceholder")}
                    {...register("body")}
                  />
                </div>

                <div className="alert alert-info py-2 small border-0 bg-light d-flex align-items-center gap-2">
                  <i className="bi bi-info-circle-fill text-info"></i>
                  <span>{t("push.emojiInfo")}</span>
                </div>

                <div className="d-flex justify-content-end mt-4">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="btn btn-primary px-4 py-2 d-flex align-items-center gap-2"
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm"></span>
                        {t("push.sending")}
                      </>
                    ) : (
                      <>
                        <i className="bi bi-send"></i>
                        <span>{t("push.send")}</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
